import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Select, MenuItem, FormControl, InputLabel, TextField, LinearProgress, FormHelperText,
  Switch, FormControlLabel, Checkbox, ListItemText
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  AutoAwesome as SparklesIcon,
  CheckCircle as CheckIcon,
  PhotoCamera as SinglePhotoIcon,
  PhotoLibrary as MultiPhotoIcon,
  Folder as FolderIcon
} from '@mui/icons-material'
import AppHeader from '../components/common/AppHeader'
import { useAuth } from '../contexts/AuthContext'
import { StylistaProvider, useStylista } from '../contexts/StylistaContext'
import apiClient from '../api/client'
import GalleryModal, { GalleryItem } from '../components/stylista/GalleryModal'

type GalleryModalType = 'faces' | 'poses' | 'figures' | 'backgrounds' | 'garments' | null

const STYLISTA_IMAGE_SLOTS = [
  { key: 'faceImage', promptLine: "use the model's face (identity reference)", title: 'Model face' },
  { key: 'poseImage', promptLine: "use the model's pose (body posture)", title: 'Pose reference' },
  { key: 'silhouetteImage', promptLine: "use the model's body shape", title: 'Body/silhouette' },
  { key: 'backgroundImage', promptLine: 'use as background/environment', title: 'Background' },
  { key: 'lightingImage', promptLine: 'use as lighting style/look', title: 'Lighting' },
  { key: 'garmentTopImage', promptLine: 'use the photo to dress the model', title: 'Ubranie góra' },
  { key: 'garmentBottomImage', promptLine: 'use the photo to dress the model', title: 'Ubranie dół' },
  { key: 'garmentAdditionalImage', promptLine: 'use the photo to dress the model', title: 'Dodatkowe ubranie' },
  { key: 'garmentExtraImage', promptLine: 'use the photo to dress the model', title: 'Dodatkowe ubranie 2' },
  { key: 'bagImage', promptLine: 'bag', title: 'Torebka' },
  { key: 'glassesImage', promptLine: 'glasses', title: 'Okulary' },
  { key: 'shoesImage', promptLine: 'shoes', title: 'Buty' },
  { key: 'jewelryImage', promptLine: 'jewelry', title: 'Biżuteria' }
] as const

type AdvancedSlotKey = typeof STYLISTA_IMAGE_SLOTS[number]['key']

interface FileUploadSlotProps {
  label: string
  description: string
  file: File | null
  onFileSelect: (file: File | null) => void
  color?: string
  dataTestId?: string
}

const FileUploadSlot: React.FC<FileUploadSlotProps> = ({ label, description, file, onFileSelect, color, dataTestId }) => {
  const [dragOver, setDragOver] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState(false)

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null)
      setPreviewError(false)
      return
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    setPreviewError(false)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file])

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f && f.type.startsWith('image/')) onFileSelect(f)
  }

  return (
    <div
      className={`flex-1 border rounded-xl transition-all ${
        dragOver ? 'border-indigo-400 bg-indigo-50/40 border-2'
        : file ? 'border-green-400 bg-white'
        : 'border-gray-300 bg-white'
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="p-3">
        {file && previewUrl ? (
          <div className="relative">
            {previewError ? (
              <div className="w-full h-40 rounded-lg border border-dashed border-red-300 flex items-center justify-center text-center px-3 mb-2">
                <p className="text-sm text-red-500">Nie mogę wczytać tego obrazu. Usuń go i załaduj ponownie.</p>
              </div>
            ) : (
              <img
                src={previewUrl} alt={label}
                onError={() => setPreviewError(true)}
                className="w-full h-40 object-contain rounded-lg mb-2"
              />
            )}
            <button
              onClick={() => onFileSelect(null)}
              className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
            >
              <CloseIcon fontSize="small" />
            </button>
            <div className="flex items-center gap-1 mt-1">
              <CheckIcon className="text-green-500" fontSize="small" />
              <span className="text-sm text-green-600 font-medium truncate">{file.name}</span>
            </div>
            <span className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</span>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <ImageIcon className="text-green-600" style={{ fontSize: 28 }} />
            </div>
            <p className="font-semibold text-sm text-gray-700 mb-1 text-center">{label}</p>
            <p className="text-xs text-gray-400 text-center mb-3">{description}</p>
            <label className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-xs text-gray-600 hover:border-indigo-400 cursor-pointer transition-colors min-h-[36px]">
              <UploadIcon fontSize="small" />
              Wybierz plik
              <input
                type="file"
                className="hidden"
                accept="image/*"
                data-testid={dataTestId}
                onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
              />
            </label>
            <p className="text-xs text-gray-400 mt-2">Możesz też przeciągnąć i upuścić obraz</p>
          </div>
        )}
      </div>
    </div>
  )
}

interface MultiImageListCardProps {
  title: string
  description: string
  files: File[]
  onAddFiles: (files: File[]) => void
  onRemoveFile: (index: number) => void
  onClear: () => void
  color?: string
  emptyState?: string
  selectionLabel?: (count: number) => string
}

const MultiImageListCard: React.FC<MultiImageListCardProps> = ({
  title,
  description,
  files,
  onAddFiles,
  onRemoveFile,
  onClear,
  color,
  emptyState,
  selectionLabel
}) => {
  const [dragOver, setDragOver] = useState(false)
  const [previews, setPreviews] = useState<string[]>([])

  useEffect(() => {
    const created: string[] = []
    const urls = files.map(file => {
      const url = URL.createObjectURL(file)
      created.push(url)
      return url
    })
    setPreviews(urls)
    return () => {
      created.forEach(url => URL.revokeObjectURL(url))
    }
  }, [files])

  const handleAdd = (list: FileList | File[] | null) => {
    if (!list) return
    const arr = Array.from(list).filter((f: File) => f.type.startsWith('image/'))
    if (arr.length) onAddFiles(arr)
  }

  const labelBuilder = selectionLabel || ((count: number) => {
    if (count === 1) return 'Wybrano 1 zdjęcie'
    if (count >= 2 && count <= 4) return `Wybrano ${count} zdjęcia`
    return `Wybrano ${count} zdjęć`
  })

  return (
    <div
      className={`flex-1 border rounded-xl transition-all ${dragOver ? 'border-indigo-400 border-2 bg-indigo-50/20' : files.length ? 'border-green-400' : 'border-gray-200'}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleAdd(e.dataTransfer.files) }}
    >
      <div className="p-3 flex flex-col gap-2">
        <div>
          <p className="font-semibold text-sm text-gray-700">{title}</p>
          <p className="text-xs text-gray-400">{description}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:border-indigo-400 cursor-pointer transition-colors min-h-[36px]">
            <UploadIcon fontSize="small" />
            Dodaj zdjęcia
            <input
              type="file" className="hidden" accept="image/*" multiple
              onChange={(e) => { handleAdd(e.target.files); if (e.target) e.target.value = '' }}
            />
          </label>
          <button
            onClick={onClear}
            disabled={!files.length}
            className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40 min-h-[36px] px-2"
          >
            Wyczyść listę
          </button>
          <span className="text-xs text-gray-400">{labelBuilder(files.length)}</span>
        </div>
        {files.length ? (
          <div className="max-h-56 overflow-y-auto flex flex-col gap-1.5">
            {files.map((file, idx) => (
              <div
                key={`${file.name}-${file.lastModified}-${idx}`}
                className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-gray-50/50"
              >
                {previews[idx] && (
                  <img src={previews[idx]} alt={file.name} className="w-12 h-12 object-cover rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-700 truncate">#{idx + 1} {file.name}</p>
                  <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button onClick={() => onRemoveFile(idx)} className="p-1.5 hover:bg-gray-200 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-300">{emptyState || 'Nie dodano jeszcze żadnych zdjęć. Użyj przycisku powyżej lub przeciągnij pliki do tej karty.'}</p>
        )}
      </div>
    </div>
  )
}

// Emerald & Champagne color palette
const LUXURY_COLORS = {
  gold: '#2E8B57',        // sea green (accent)
  goldLight: '#E8F5E9',   // mint tint (hover bg)
  black: '#1D4A3D',       // forest (dark text/buttons)
  white: '#ffffff',
  gray: '#F5F0E6',        // champagne (tile header bg)
  border: '#D4E4D9'       // soft green border
}

interface ReferenceTileProps {
  children: React.ReactNode
  title?: string
  mode?: 'single' | 'batch'
  onModeChange?: (mode: 'single' | 'batch') => void
  hasGallery?: boolean
  onGalleryClick?: () => void
  accentColor?: string
}

const ReferenceTile: React.FC<ReferenceTileProps> = ({
  children,
  title,
  mode,
  onModeChange,
  hasGallery,
  onGalleryClick,
  accentColor = LUXURY_COLORS.gold
}) => (
  <div className="flex flex-col border border-gray-300 rounded-xl bg-white shadow-sm hover:shadow-md hover:border-green-400 transition-all overflow-hidden min-h-[280px] w-full">
    {/* Header */}
    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-stone-100 min-h-[48px]">
      <div className="flex items-center gap-1">
        {onModeChange ? (
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => onModeChange('single')}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${
                mode === 'single' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SinglePhotoIcon style={{ fontSize: 14 }} />
              Pojedyncze
            </button>
            <button
              onClick={() => onModeChange('batch')}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors border-l border-gray-200 ${
                mode === 'batch' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <MultiPhotoIcon style={{ fontSize: 14 }} />
              Lista
            </button>
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-medium">{title || ''}</span>
        )}
      </div>
      {hasGallery && (
        <button
          onClick={onGalleryClick}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition-colors min-h-[32px]"
        >
          <FolderIcon style={{ fontSize: 14 }} />
          Galeria
        </button>
      )}
    </div>
    {/* Content area */}
    <div className="flex flex-grow p-2">
      {children}
    </div>
  </div>
)

const StylistaContent: React.FC = () => {
  const { projectName } = useParams<{ projectName: string }>()
  const { user, logout, isAdmin } = useAuth()
  const {
    faceImage, faceMode, faceBatch, setFaceMode, addFaceFiles, removeFaceFile, clearFaceBatch,
    poseImage, poseMode, poseBatch, setPoseMode, addPoseFiles, removePoseFile, clearPoseBatch,
    garmentTopMode, garmentTopBatch, setGarmentTopMode, addGarmentTopFiles, removeGarmentTopFile, clearGarmentTopBatch,
    garmentBottomMode, garmentBottomBatch, setGarmentBottomMode, addGarmentBottomFiles, removeGarmentBottomFile, clearGarmentBottomBatch,
    silhouetteImage, backgroundImage, lightingImage,
    garmentTopImage, garmentBottomImage, garmentAdditionalImage, garmentExtraImage, bagImage, glassesImage, shoesImage, jewelryImage,
    setFile, selectedModel, setSelectedModel, selectedModels, setSelectedModels, multiModelEnabled, setMultiModelEnabled,
    aspectRatio, setAspectRatio, imageSize, setImageSize,
    resolution, setResolution, outputFormat, setOutputFormat,
    plumSize, setPlumSize, plumQuality, setPlumQuality, transparentBackground, setTransparentBackground,
    fullPromptOverride, setFullPromptOverride,
    prompt, setPrompt, promptPresetId, setPromptPresetId, promptPresets, setPromptPresets,
    generateImage, generationResult, resetState
  } = useStylista()

  const [galleryFaces, setGalleryFaces] = useState<GalleryItem[]>([])
  const [galleryPoses, setGalleryPoses] = useState<GalleryItem[]>([])
  const [galleryFigures, setGalleryFigures] = useState<GalleryItem[]>([])
  const [galleryBackgrounds, setGalleryBackgrounds] = useState<GalleryItem[]>([])
  const [galleryGarments, setGalleryGarments] = useState<GalleryItem[]>([])
  const [garmentGallerySlot, setGarmentGallerySlot] = useState<'garmentTopImage' | 'garmentBottomImage'>('garmentTopImage')
  const [galleryLoading, setGalleryLoading] = useState<Record<string, boolean>>({})
  const [galleryError, setGalleryError] = useState<string | null>(null)
  const [galleryModalType, setGalleryModalType] = useState<GalleryModalType>(null)

  // Photography style injection
  const [photoStyleId, setPhotoStyleId] = useState<string>('')
  const [photoStyles, setPhotoStyles] = useState<Array<{ style_id: string; name: string; prompt_injection: string }>>([])

  useEffect(() => {
    fetch('/stylista/styles/index.json')
      .then(r => r.json())
      .then(data => setPhotoStyles(data))
      .catch(() => {/* style library optional */})
  }, [])

  const photoStyleInjection = photoStyles.find(s => s.style_id === photoStyleId)?.prompt_injection || ''

  // Capabilities fetched from backend
  const [blueberryResOptions, setBlueberryResOptions] = useState<string[]>(['2k','2k_portrait','4k','4k_portrait','fashion_4k'])
  const [blueberryFormats, setBlueberryFormats] = useState<string[]>(['PNG','JPEG','WEBP','AVIF'])
  const [blueberry45ResOptions, setBlueberry45ResOptions] = useState<string[]>(['1k','2k','2k_portrait','4k','4k_portrait','fashion_4k'])
  const [blueberry45Formats, setBlueberry45Formats] = useState<string[]>(['PNG','JPEG'])
  const [strawberryResOptions, setStrawberryResOptions] = useState<string[]>(['1K','2K'])
  const [strawberryProResOptions, setStrawberryProResOptions] = useState<string[]>(['1K','2K','4K'])
  const [lemonLuxResOptions, setLemonLuxResOptions] = useState<string[]>(['1K','2K'])
  // Plum v2 family (gpt-image-2) — explicit pixel sizes + quality knob. Static option lists.
  const plumV2SizeOptions = [
    { value: '1024x1536', label: 'Portret (2:3) - 1024x1536' },
    { value: '1024x1024', label: 'Kwadrat (1:1) - 1024x1024' },
    { value: '1536x1152', label: 'Standard (4:3) - 1536x1152' },
    { value: '1152x2048', label: 'Pion (9:16) - 1152x2048' },
    { value: '2048x1152', label: 'Poziom (16:9) - 2048x1152' },
    { value: '2048x2048', label: 'Kwadrat HD (1:1) - 2048x2048' },
    { value: '2160x3840', label: 'Pion 4K (9:16) - 2160x3840' },
    { value: '3840x2160', label: 'Poziom 4K (16:9) - 3840x2160' },
    { value: 'auto', label: 'Auto' },
  ] as const
  const plumV2QualityOptions = ['low', 'medium', 'high', 'auto'] as const
  const sunburstQualityOptions = ['low', 'medium', 'high', 'xhigh', 'max', 'auto'] as const
  const [noModelsAvailable, setNoModelsAvailable] = useState(false)

  const isGenerating = generationResult.status === 'generating'
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  useEffect(() => {
    if (!isGenerating) { setElapsedSeconds(0); return }
    const start = Date.now()
    const id = setInterval(() => setElapsedSeconds(Math.floor((Date.now() - start) / 1000)), 1000)
    return () => clearInterval(id)
  }, [isGenerating])
  const isBlueberryModel = selectedModel === 'blueberry' || selectedModel === 'blueberry-45'
  const isLemonModel = selectedModel === 'lemon-lux' || selectedModel === 'lemon-max' || selectedModel === 'lemon-max2-edit'
  const isPlumV2Model = selectedModel === 'plum-v2' || selectedModel === 'plumv2-fast'
  const isSunburstModel = selectedModel === 'sunburst'
  const isFlareModel = selectedModel === 'flare'
  const isOpenAIImageModel = isPlumV2Model || isSunburstModel || isFlareModel
  const supportsTransparentBackground = selectedModel === 'plumv2-fast' || isSunburstModel || isFlareModel
  const showGeminiResolution = selectedModel === 'strawberry' || selectedModel === 'strawberry-pro' || isLemonModel
  const currentGeminiResOptions = selectedModel === 'strawberry-pro'
    ? strawberryProResOptions
    : isLemonModel
      ? lemonLuxResOptions
      : strawberryResOptions
  const currentBlueberryResOptions = selectedModel === 'blueberry-45' ? blueberry45ResOptions : blueberryResOptions

  const modelLabel = (codename: string) => {
    const base: Record<string, string> = {
      'strawberry-pro2': 'Strawberry Pro 2',
      'strawberry-pro': 'Strawberry Pro',
      strawberry: 'Strawberry',
      blueberry: 'Blueberry',
      'blueberry-45': 'Blueberry 4.5',
      'lemon-lux': 'Lemon Lux',
      'lemon-max': 'Lemon Max',
      'lemon-max2-edit': 'Lemon Max2 Edit',
      'plum-v2': 'Plum v2',
      'plumv2-fast': 'Plum v2 Fast',
      sunburst: 'Sunburst',
      flare: 'Flare (GPT Image 2.5)',
    }
    const originAdminOnly: Record<string, string> = {
      'lemon-lux': 'Black Forest',
      'lemon-max': 'AimlAPI',
      'lemon-max2-edit': 'Black Forest',
      'plum-v2': 'OpenAI Codex',
      'plumv2-fast': 'OpenAI API',
      sunburst: 'OpenAI API',
      flare: 'OpenAI API',
    }
    const label = base[codename] || codename
    const origin = isAdmin ? originAdminOnly[codename] : null
    return origin ? `${label} (${origin})` : label
  }
  const currentBlueberryFormats = selectedModel === 'blueberry-45' ? blueberry45Formats : blueberryFormats
  const hasFace = faceMode === 'batch' ? faceBatch.length > 0 : Boolean(faceImage)
  const hasPose = poseMode === 'batch' ? poseBatch.length > 0 : Boolean(poseImage)
  const hasGarmentTop = garmentTopMode === 'batch' ? garmentTopBatch.length > 0 : Boolean(garmentTopImage)
  const hasGarmentBottom = garmentBottomMode === 'batch' ? garmentBottomBatch.length > 0 : Boolean(garmentBottomImage)
  const hasFiles = !!(
    hasFace ||
    hasPose ||
    hasGarmentTop ||
    hasGarmentBottom ||
    garmentExtraImage ||
    garmentAdditionalImage ||
    silhouetteImage ||
    backgroundImage ||
    lightingImage ||
    bagImage ||
    glassesImage ||
    shoesImage ||
    jewelryImage
  )
  const canGenerate = hasFiles && !isGenerating && !noModelsAvailable

  const selectedPromptPreset = useMemo(() => (
    promptPresets.find(preset => preset.id === promptPresetId)
  ), [promptPresets, promptPresetId])

  const combinedPromptPreview = useMemo(() => {
    const parts: string[] = []
    const presetText = selectedPromptPreset?.prompt?.trim()
    const userText = prompt?.trim()
    if (presetText) parts.push(presetText)
    if (userText) parts.push(userText)
    return parts.join('\n\n').trim()
  }, [selectedPromptPreset, prompt])

  // Advanced preview: prompt + file order (mirrors backend _build_stylista_prompt + ordered_slots)
  const slotFiles = useMemo<Record<AdvancedSlotKey, File[]>>(() => ({
    faceImage: faceMode === 'batch'
      ? faceBatch
      : (faceImage ? [faceImage] : []),
    poseImage: poseMode === 'batch'
      ? poseBatch
      : (poseImage ? [poseImage] : []),
    silhouetteImage: silhouetteImage ? [silhouetteImage] : [],
    backgroundImage: backgroundImage ? [backgroundImage] : [],
    lightingImage: lightingImage ? [lightingImage] : [],
    garmentTopImage: garmentTopMode === 'batch'
      ? garmentTopBatch
      : (garmentTopImage ? [garmentTopImage] : []),
    garmentBottomImage: garmentBottomMode === 'batch'
      ? garmentBottomBatch
      : (garmentBottomImage ? [garmentBottomImage] : []),
    garmentAdditionalImage: garmentAdditionalImage ? [garmentAdditionalImage] : [],
    garmentExtraImage: garmentExtraImage ? [garmentExtraImage] : [],
    bagImage: bagImage ? [bagImage] : [],
    glassesImage: glassesImage ? [glassesImage] : [],
    shoesImage: shoesImage ? [shoesImage] : [],
    jewelryImage: jewelryImage ? [jewelryImage] : []
  }), [
    faceImage,
    faceBatch,
    faceMode,
    poseImage,
    poseBatch,
    poseMode,
    silhouetteImage,
    backgroundImage,
    lightingImage,
    garmentTopImage,
    garmentTopBatch,
    garmentTopMode,
    garmentBottomImage,
    garmentBottomBatch,
    garmentBottomMode,
    garmentAdditionalImage,
    garmentExtraImage,
    bagImage,
    glassesImage,
    shoesImage,
    jewelryImage
  ])

  const promptRefreshKey = useMemo(() => {
    const entries = Object.entries(slotFiles).map(([key, files]) => {
      const stamp = files.map((file) => `${file.name}-${file.size}-${file.lastModified}`).join(',')
      return `${key}:${stamp}`
    })
    return entries.join('|')
  }, [slotFiles])

  const pickGalleryImage = async (item: GalleryItem, slot: AdvancedSlotKey) => {
    const src = item.image_url || item.thumbnail_url || ''
    if (!src) {
      setGalleryError('Brak URL do obrazu w galerii.')
      return
    }
    setGalleryError(null)
    try {
      const res = await fetch(src, { mode: 'cors' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const extFromType = blob.type.includes('png') ? 'png' : (blob.type.includes('webp') ? 'webp' : 'jpg')
      const ext = (src.split('.').pop() || '').split('?')[0] || extFromType
      const safeName = (item.name || 'gallery').replace(/\s+/g, '-')
      const file = new File([blob], `${safeName}.${ext}`, { type: blob.type || 'image/jpeg' })
      setFile(slot, file)
    } catch {
      setGalleryError('Nie mogę pobrać obrazu z galerii. Sprawdź dostęp do S3 i CORS.')
    }
  }

  const loadGallery = async (kind: 'faces' | 'poses' | 'figures' | 'backgrounds' | 'garments') => {
    if (galleryLoading[kind]) return
    setGalleryLoading(prev => ({ ...prev, [kind]: true }))
    setGalleryError(null)
    try {
      if (kind === 'faces') {
        const resp = await apiClient.get('/mobile/v1/presets/faces')
        setGalleryFaces(Array.isArray(resp?.data?.faces) ? resp.data.faces : [])
      } else if (kind === 'poses') {
        // Try API first, fallback to local static manifest (BUG 3 — brak obrazków na dev)
        let items: GalleryItem[] = []
        try {
          const resp = await apiClient.get('/mobile/v1/presets/poses')
          items = Array.isArray(resp?.data?.poses) ? resp.data.poses : []
        } catch { /* API unavailable — use local fallback below */ }
        if (items.length === 0) {
          const r = await fetch('/stylista/poses/manifest.json')
          const data = await r.json()
          const raw = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : [])
          items = raw.map((item: any) => ({
            id: item.id || item.pose_name,
            name: item.name,
            thumbnail_url: `/stylista/poses/${item.thumbnail || item.file}`,
            image_url: `/stylista/poses/${item.file}`,
            gender: item.gender,
            body_part: item.body_part,
            pose_name: item.pose_name,
            group: item.group,
          }))
        }
        setGalleryPoses(items)
      } else if (kind === 'figures') {
        // Always use basic-manifest.json — canonical source with categories and metadata
        // (legacy /mobile/v1/presets/silhouettes API returns stale data without group field)
        const r = await fetch('/stylista/figure/basic-manifest.json')
        const data = await r.json()
        const raw = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : [])
        const items: GalleryItem[] = raw.map((item: any) => ({
          id: item.id || item.name,
          name: item.name,
          thumbnail_url: `/stylista/figure/${item.thumbnail || item.file}`,
          image_url: `/stylista/figure/${item.file}`,
          gender: item.gender,
          figure_type: item.figure_type,
          description: item.description,
          height_range: item.height_range,
          group: item.group,
        }))
        setGalleryFigures(items)
      } else if (kind === 'backgrounds') {
        const resp = await apiClient.get('/mobile/v1/presets/backgrounds')
        setGalleryBackgrounds(Array.isArray(resp?.data?.backgrounds) ? resp.data.backgrounds : [])
      } else if (kind === 'garments') {
        // SSOT: static JSON manifests (female-clothes.json / male-clothes.json)
        // Unified across all envs — mobile API returned different data per env (#56)
        const [femaleR, maleR] = await Promise.allSettled([
          fetch('/stylista/clothes/female/female-clothes.json'),
          fetch('/stylista/clothes/male/male-clothes.json'),
        ])
        const allClothes: any[] = []
        if (femaleR.status === 'fulfilled') {
          const data = await femaleR.value.json().catch(() => [])
          if (Array.isArray(data)) allClothes.push(...data)
        }
        if (maleR.status === 'fulfilled') {
          const data = await maleR.value.json().catch(() => [])
          if (Array.isArray(data)) allClothes.push(...data)
        }
        const items = allClothes
          .filter(s => s.status === 'generated')
          .map(s => ({
            id: s.name,
            name: s.name.replace(/_/g, ' '),
            thumbnail_url: `/stylista/clothes/${s.path}/${s.name}_${s.generated_suffix || 'strawberry_pro'}_miniature.jpg`,
            image_url: `/stylista/clothes/${s.path}/${s.name}_${s.generated_suffix || 'strawberry_pro'}.jpg`,
            group: s.group || s.category?.type,
            subgroup: s.subgroup || s.category?.type,
            gender: s.category?.gender,
          }))
        setGalleryGarments(items)
      }
    } catch {
      setGalleryError('Nie udało się wczytać galerii z S3.')
    } finally {
      setGalleryLoading(prev => ({ ...prev, [kind]: false }))
    }
  }

  const openGarmentGallery = (slot: 'garmentTopImage' | 'garmentBottomImage') => {
    setGarmentGallerySlot(slot)
    const alreadyLoaded = galleryGarments.length > 0
    if (!alreadyLoaded && !galleryLoading['garments']) loadGallery('garments')
    setGalleryModalType('garments')
  }

  const openGallery = (type: GalleryModalType) => {
    if (type && !galleryLoading[type]) {
      const alreadyLoaded = (type === 'faces' && galleryFaces.length > 0)
        || (type === 'poses' && galleryPoses.length > 0)
        || (type === 'figures' && galleryFigures.length > 0)
        || (type === 'backgrounds' && galleryBackgrounds.length > 0)
        || (type === 'garments' && galleryGarments.length > 0)
      if (!alreadyLoaded) {
        loadGallery(type)
      }
    }
    setGalleryModalType(type)
  }

  const getGalleryItems = (): GalleryItem[] => {
    switch (galleryModalType) {
      case 'faces': return galleryFaces
      case 'poses': return galleryPoses
      case 'figures': return galleryFigures
      case 'backgrounds': return galleryBackgrounds
      case 'garments': return galleryGarments
      default: return []
    }
  }

  const getGalleryTitle = (): string => {
    switch (galleryModalType) {
      case 'faces': return 'Galeria twarzy'
      case 'poses': return 'Galeria póz'
      case 'figures': return 'Galeria sylwetek'
      case 'backgrounds': return 'Galeria teł'
      case 'garments': return 'Galeria ubrań'
      default: return 'Galeria'
    }
  }

  const handleGallerySelect = (item: GalleryItem) => {
    const slotMap: Record<string, AdvancedSlotKey> = {
      faces: 'faceImage',
      poses: 'poseImage',
      figures: 'silhouetteImage',
      backgrounds: 'backgroundImage',
      garments: garmentGallerySlot,
    }
    if (galleryModalType) {
      pickGalleryImage(item, slotMap[galleryModalType])
    }
  }

  useEffect(() => {
    if (fullPromptOverride) {
      setFullPromptOverride('')
    }
  }, [prompt, promptPresetId, promptRefreshKey, setFullPromptOverride])

  const advancedSlotData = useMemo(() => {
    let idx = 1
    return STYLISTA_IMAGE_SLOTS.map(slot => {
      const files = slotFiles[slot.key] || []
      const present = files.length > 0
      const assignedIndex = present ? idx++ : null
      return { ...slot, files, present, assignedIndex }
    })
  }, [slotFiles])

  const [previewUrls, setPreviewUrls] = useState<Partial<Record<AdvancedSlotKey, string[]>>>({})

  useEffect(() => {
    const nextUrls: Partial<Record<AdvancedSlotKey, string[]>> = {}
    const created: string[] = []
    STYLISTA_IMAGE_SLOTS.forEach(slot => {
      const files = slotFiles[slot.key]
      if (files?.length) {
        nextUrls[slot.key] = files.map(file => {
          const url = URL.createObjectURL(file)
          created.push(url)
          return url
        })
      }
    })
    setPreviewUrls(nextUrls)
    return () => {
      created.forEach(url => URL.revokeObjectURL(url))
    }
  }, [slotFiles])

  const advancedPrompt = useMemo(() => {
    const header: string[] = ['Reference images (order matters):']
    advancedSlotData.forEach(slot => {
      if (slot.present && slot.assignedIndex) {
        header.push(`Image ${slot.assignedIndex}: ${slot.promptLine}.`)
      }
    })
    if (header.length > 1) {
      header.push('Keep the same face/identity across outputs.')
    }

    const sections: string[] = []
    if (combinedPromptPreview) {
      sections.push(`User prompt:\n${combinedPromptPreview}`)
    }

    const body = sections.join('\n\n').trim()
    const fallbackBody = (
      "Task:\n" +
      "Create a photorealistic fashion image using the reference images. " +
      "Preserve the model identity and apply the provided garments and accessories."
    )
    return (header.join('\n') + `\n\n${body || fallbackBody}`).trim()
  }, [advancedSlotData, combinedPromptPreview])

  const batchInfo = generationResult.batchInfo
  const resultPreviews = useMemo(() => {
    if (batchInfo?.results?.length) {
      return batchInfo.results.flatMap((job) => (
        (job.previewUrls || []).map((url) => ({
          url,
          modelName: job.modelName || null,
          metadata: null as any,
        }))
      ))
    }
    return (generationResult.previewUrls || []).map((url) => ({
      url,
      modelName: null,
      // Single-job path carries metadata (size/quality/generation_time/codename) from model.generate().
      metadata: generationResult.metadata || null,
    }))
  }, [batchInfo, generationResult.previewUrls, generationResult.metadata])

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const resp = await apiClient.get('/stylista/models?module=stylista')
        const models = resp?.data?.models || {}
        if (!active) return
        const bb = models?.blueberry || {}
        if (bb) {
          if (Array.isArray(bb.resolution_options) && bb.resolution_options.length) {
            setBlueberryResOptions(bb.resolution_options)
          }
          if (Array.isArray(bb.output_formats) && bb.output_formats.length) {
            setBlueberryFormats(bb.output_formats)
          }
        }
        const bb45 = models?.['blueberry-45'] || {}
        if (bb45) {
          if (Array.isArray(bb45.resolution_options) && bb45.resolution_options.length) {
            setBlueberry45ResOptions(bb45.resolution_options)
          }
          if (Array.isArray(bb45.output_formats) && bb45.output_formats.length) {
            setBlueberry45Formats(bb45.output_formats)
          }
        }
        const straw = models?.strawberry || {}
        const strawPro = models?.['strawberry-pro'] || {}
        const lemonLux = models?.['lemon-lux'] || {}
        const lemonMax = models?.['lemon-max'] || {}
        const lemonMax2Edit = models?.['lemon-max2-edit'] || {}
        const normalize = (arr: any) => Array.isArray(arr) ? arr.map((item: string) => item.toUpperCase()) : null
        const strawRes = normalize(straw.resolutions || straw.supported_resolutions)
        if (strawRes?.length) setStrawberryResOptions(strawRes)
        const strawProRes = normalize(strawPro.supported_resolutions || strawPro.resolutions)
        if (strawProRes?.length) setStrawberryProResOptions(strawProRes)
        const lemonRes =
          normalize(lemonMax2Edit.supported_resolutions || lemonMax2Edit.resolutions) ||
          normalize(lemonMax.supported_resolutions || lemonMax.resolutions) ||
          normalize(lemonLux.supported_resolutions || lemonLux.resolutions)
        if (lemonRes?.length) setLemonLuxResOptions(lemonRes)
        const presetsFromApi = resp?.data?.prompt_presets
        if (Array.isArray(presetsFromApi) && presetsFromApi.length) {
          setPromptPresets(presetsFromApi)
          const hasCurrent = presetsFromApi.some((preset: any) => preset?.id === promptPresetId)
          if (!hasCurrent) {
            const fallbackPreset = presetsFromApi.find((preset: any) => preset?.id) || presetsFromApi[0]
            if (fallbackPreset?.id) setPromptPresetId(fallbackPreset.id)
          }
        }
        // Auto-select recommended model if current model is unavailable
        const recommended = resp?.data?.recommended
        if (recommended && models && typeof models === 'object') {
          const currentAvailable = (models as any)?.[selectedModel]?.status !== 'disabled'
          if (!currentAvailable) {
            setSelectedModel(recommended as any)
          }
        }
        // Check if all models are disabled
        const allDisabled = Object.values(models).every((m: any) => (m as any)?.status === 'disabled')
        setNoModelsAvailable(allDisabled)
      } catch {}
    })()
    return () => { active = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let allowed: string[] | null = null
    if (selectedModel === 'strawberry') {
      allowed = strawberryResOptions
    } else if (selectedModel === 'strawberry-pro') {
      allowed = strawberryProResOptions
    } else if (selectedModel === 'lemon-lux' || selectedModel === 'lemon-max' || selectedModel === 'lemon-max2-edit') {
      allowed = lemonLuxResOptions
    }
    if (allowed && allowed.length && !allowed.includes(imageSize)) {
      const fallback = allowed.includes('2K') ? '2K' : allowed[0]
      if (fallback) setImageSize(fallback as any)
    }
  }, [selectedModel, strawberryResOptions, strawberryProResOptions, lemonLuxResOptions, imageSize, setImageSize])

  const handleGenerate = async () => { await generateImage(projectName || 'Stylista', photoStyleInjection || undefined) }

  // Derive aspect ratio from Blueberry resolution (for display + state sync)
  const derivedBlueberryAR = (res: string): '1:1' | '1:2' | '2:3' => {
    switch (res) {
      case '2k':
      case '4k':
      case '1k':
        return '1:1'
      case '2k_portrait':
      case '4k_portrait':
        return '1:2'
      case 'fashion_4k':
      default:
        return '2:3'
    }
  }

  // Keep aspectRatio consistent for Blueberry (informational only)
  useEffect(() => {
    if (isBlueberryModel) {
      const ar = derivedBlueberryAR(resolution)
      if (aspectRatio !== ar) setAspectRatio(ar as any)
    }
  }, [selectedModel, resolution, isBlueberryModel])

  return (
    <>
      <AppHeader title={`Stylista - ${projectName || 'Stylista'}`} username={user?.username} showAdminButton={isAdmin} showBackButton onLogout={logout} />
      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Header card */}
        <div className="flex items-center gap-3 mb-6 p-4 bg-indigo-50/40 border border-indigo-100 rounded-xl">
          <SparklesIcon className="text-indigo-600" style={{ fontSize: 32 }} />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Stylista</h1>
            <p className="text-sm text-gray-500">Wgraj referencje: twarz, poza, sylwetka, tło, oświetlenie. Wybierz model i wygeneruj.</p>
          </div>
        </div>

        {/* Reference images section */}
        <details open className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
          <summary className="px-4 py-3 bg-white font-semibold text-gray-700 cursor-pointer flex items-center justify-between list-none select-none">
            Zdjęcia referencyjne
            <span className="text-xs text-gray-400">▼</span>
          </summary>
          <div className="p-4 border-t border-gray-100 bg-white">
            {galleryError && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 mb-3 text-sm text-yellow-700">{galleryError}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">

              {/* Face tile */}
              <div className="flex">
                <ReferenceTile mode={faceMode} onModeChange={setFaceMode} hasGallery onGalleryClick={() => openGallery('faces')}>
                  {faceMode === 'single' ? (
                    <FileUploadSlot
                      label="Twarz (Image 1)"
                      description="Tożsamość modelki"
                      file={faceImage}
                      onFileSelect={(f) => setFile('faceImage', f)}
                      color={LUXURY_COLORS.gold}
                      dataTestId="stylista-upload-face"
                    />
                  ) : (
                    <MultiImageListCard
                      title="Lista twarzy (Image 1)"
                      description="Dodaj wiele referencji twarzy."
                      files={faceBatch}
                      onAddFiles={addFaceFiles}
                      onRemoveFile={removeFaceFile}
                      onClear={clearFaceBatch}
                      color={LUXURY_COLORS.gold}
                      emptyState="Nie dodano jeszcze żadnych twarzy."
                      selectionLabel={(count) => {
                        if (count === 1) return 'Wybrano 1 twarz'
                        if (count >= 2 && count <= 4) return `Wybrano ${count} twarze`
                        return `Wybrano ${count} twarzy`
                      }}
                    />
                  )}
                </ReferenceTile>
              </div>

              {/* Pose tile */}
              <div className="flex">
                <ReferenceTile mode={poseMode} onModeChange={setPoseMode} hasGallery onGalleryClick={() => openGallery('poses')}>
                  {poseMode === 'single' ? (
                    <FileUploadSlot
                      label="Poza (Image 2)"
                      description="Ułożenie ciała"
                      file={poseImage}
                      onFileSelect={(f) => setFile('poseImage', f)}
                      color={LUXURY_COLORS.gold}
                      dataTestId="stylista-upload-pose"
                    />
                  ) : (
                    <MultiImageListCard
                      title="Lista póz (Image 2)"
                      description="Dodaj wiele zdjęć z pozami."
                      files={poseBatch}
                      onAddFiles={addPoseFiles}
                      onRemoveFile={removePoseFile}
                      onClear={clearPoseBatch}
                      color={LUXURY_COLORS.gold}
                      emptyState="Nie dodano jeszcze żadnych póz."
                      selectionLabel={(count) => {
                        if (count === 1) return 'Wybrano 1 pozę'
                        if (count >= 2 && count <= 4) return `Wybrano ${count} pozycje`
                        return `Wybrano ${count} póz`
                      }}
                    />
                  )}
                </ReferenceTile>
              </div>

              {/* Silhouette tile */}
              <div className="flex">
                <ReferenceTile title="Sylwetka" hasGallery onGalleryClick={() => openGallery('figures')}>
                  <FileUploadSlot
                    label="Sylwetka (Image 3)"
                    description="Proporcje ciała"
                    file={silhouetteImage}
                    onFileSelect={(f) => setFile('silhouetteImage', f)}
                    color={LUXURY_COLORS.gold}
                    dataTestId="stylista-upload-silhouette"
                  />
                </ReferenceTile>
              </div>

              {/* Background tile */}
              <div className="flex">
                <ReferenceTile title="Tło" hasGallery onGalleryClick={() => openGallery('backgrounds')}>
                  <FileUploadSlot
                    label="Tło (Image 4)"
                    description="Środowisko"
                    file={backgroundImage}
                    onFileSelect={(f) => setFile('backgroundImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

              {/* Lighting tile */}
              <div className="flex">
                <ReferenceTile title="Oświetlenie">
                  <FileUploadSlot
                    label="Oświetlenie (Image 5)"
                    description="Styl światła"
                    file={lightingImage}
                    onFileSelect={(f) => setFile('lightingImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

              {/* Garment top tile */}
              <div className="flex">
                <ReferenceTile mode={garmentTopMode} onModeChange={setGarmentTopMode} hasGallery onGalleryClick={() => openGarmentGallery('garmentTopImage')}>
                  {garmentTopMode === 'single' ? (
                    <FileUploadSlot
                      label="Ubranie góra (Image 6)"
                      description="Zdjęcie ubrania nakładanego na górę ciała"
                      file={garmentTopImage}
                      onFileSelect={(f) => setFile('garmentTopImage', f)}
                      color={LUXURY_COLORS.gold}
                    />
                  ) : (
                    <MultiImageListCard
                      title="Lista ubrań (góra) — Image 6"
                      description="Dodaj wiele elementów garderoby dla górnej części stylizacji."
                      files={garmentTopBatch}
                      onAddFiles={addGarmentTopFiles}
                      onRemoveFile={removeGarmentTopFile}
                      onClear={clearGarmentTopBatch}
                      color={LUXURY_COLORS.gold}
                      emptyState="Nie dodano żadnych ubrań (góra). Dodaj pliki lub przeciągnij je tutaj."
                      selectionLabel={(count) => {
                        if (count === 1) return 'Wybrano 1 element (góra)'
                        if (count >= 2 && count <= 4) return `Wybrano ${count} elementy (góra)`
                        return `Wybrano ${count} elementów (góra)`
                      }}
                    />
                  )}
                </ReferenceTile>
              </div>

              {/* Garment bottom tile */}
              <div className="flex">
                <ReferenceTile mode={garmentBottomMode} onModeChange={setGarmentBottomMode} hasGallery onGalleryClick={() => openGarmentGallery('garmentBottomImage')}>
                  {garmentBottomMode === 'single' ? (
                    <FileUploadSlot
                      label="Ubranie dół (Image 7)"
                      description="Spodnie / spódnica / dół stylizacji"
                      file={garmentBottomImage}
                      onFileSelect={(f) => setFile('garmentBottomImage', f)}
                      color={LUXURY_COLORS.gold}
                    />
                  ) : (
                    <MultiImageListCard
                      title="Lista ubrań (dół) — Image 7"
                      description="Dodaj wiele elementów garderoby dla dolnej części stylizacji."
                      files={garmentBottomBatch}
                      onAddFiles={addGarmentBottomFiles}
                      onRemoveFile={removeGarmentBottomFile}
                      onClear={clearGarmentBottomBatch}
                      color={LUXURY_COLORS.gold}
                      emptyState="Nie dodano żadnych ubrań (dół)."
                      selectionLabel={(count) => {
                        if (count === 1) return 'Wybrano 1 element (dół)'
                        if (count >= 2 && count <= 4) return `Wybrano ${count} elementy (dół)`
                        return `Wybrano ${count} elementów (dół)`
                      }}
                    />
                  )}
                </ReferenceTile>
              </div>

              {/* Additional garment tile */}
              <div className="flex">
                <ReferenceTile title="Dodatkowe ubranie">
                  <FileUploadSlot
                    label="Dodatkowe ubranie (Image 8)"
                    description="Dodatkowy element garderoby"
                    file={garmentAdditionalImage}
                    onFileSelect={(f) => setFile('garmentAdditionalImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

              {/* Extra garment tile */}
              <div className="flex">
                <ReferenceTile title="Dodatkowe ubranie 2">
                  <FileUploadSlot
                    label="Dodatkowe ubranie 2 (Image 9)"
                    description="Kolejny element garderoby"
                    file={garmentExtraImage}
                    onFileSelect={(f) => setFile('garmentExtraImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

              {/* Bag tile */}
              <div className="flex">
                <ReferenceTile title="Torebka">
                  <FileUploadSlot
                    label="Torebka (Image 10)"
                    description="Referencja torebki / akcesorium"
                    file={bagImage}
                    onFileSelect={(f) => setFile('bagImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

              {/* Glasses tile */}
              <div className="flex">
                <ReferenceTile title="Okulary">
                  <FileUploadSlot
                    label="Okulary (Image 11)"
                    description="Referencja okularów"
                    file={glassesImage}
                    onFileSelect={(f) => setFile('glassesImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

              {/* Shoes tile */}
              <div className="flex">
                <ReferenceTile title="Buty">
                  <FileUploadSlot
                    label="Buty (Image 12)"
                    description="Referencja obuwia"
                    file={shoesImage}
                    onFileSelect={(f) => setFile('shoesImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

              {/* Jewelry tile */}
              <div className="flex">
                <ReferenceTile title="Biżuteria">
                  <FileUploadSlot
                    label="Biżuteria (Image 13)"
                    description="Naszyjniki, kolczyki itp."
                    file={jewelryImage}
                    onFileSelect={(f) => setFile('jewelryImage', f)}
                    color={LUXURY_COLORS.gold}
                  />
                </ReferenceTile>
              </div>

            </div>
          </div>
        </details>

        {/* Settings section */}
        <details open className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
          <summary className="px-4 py-3 bg-white font-semibold text-gray-700 cursor-pointer flex items-center justify-between list-none select-none">
            Ustawienia
            <span className="text-xs text-gray-400">▼</span>
          </summary>
          <div className="p-4 border-t border-gray-100 bg-white">
            <div className="flex flex-wrap gap-4 items-start">
              <FormControlLabel
                control={
                  <Switch
                    checked={multiModelEnabled}
                    onChange={(e) => setMultiModelEnabled(e.target.checked)}
                  />
                }
                label="Generuj na kilku modelach"
              />
              {multiModelEnabled ? (
                <FormControl sx={{ minWidth: 260 }}>
                  <InputLabel>Modele</InputLabel>
                  <Select
                    multiple
                    value={selectedModels}
                    label="Modele"
                    onChange={(e) => {
                      const raw = e.target.value as any
                      const next = Array.isArray(raw) ? raw : String(raw || '').split(',').filter(Boolean)
                      setSelectedModels(next as any)
                    }}
                    renderValue={(selected) => (selected as string[]).map(modelLabel).join(', ')}
                  >
                    {(['strawberry-pro2','strawberry-pro','strawberry','blueberry','blueberry-45','lemon-lux','lemon-max','lemon-max2-edit','plum-v2','plumv2-fast','sunburst','flare'] as const)
                      .map(model => (
                        <MenuItem key={model} value={model}>
                          <Checkbox checked={selectedModels.includes(model)} />
                          <ListItemText primary={modelLabel(model)} />
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>Tryb multi-model używa kolejki async i generuje osobne zadania dla każdej modelki.</FormHelperText>
                </FormControl>
              ) : (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={selectedModel}
                    label="Model"
                    onChange={(e) => setSelectedModel(e.target.value as any)}
                    data-testid="stylista-select-model"
                  >
                    <MenuItem value="strawberry-pro2">{modelLabel('strawberry-pro2')}</MenuItem>
                    <MenuItem value="strawberry-pro">{modelLabel('strawberry-pro')}</MenuItem>
                    <MenuItem value="strawberry">{modelLabel('strawberry')}</MenuItem>
                    <MenuItem value="blueberry">{modelLabel('blueberry')}</MenuItem>
                    <MenuItem value="blueberry-45">{modelLabel('blueberry-45')}</MenuItem>
                    <MenuItem value="lemon-lux">{modelLabel('lemon-lux')}</MenuItem>
                    <MenuItem value="lemon-max">{modelLabel('lemon-max')}</MenuItem>
                    <MenuItem value="lemon-max2-edit">{modelLabel('lemon-max2-edit')}</MenuItem>
                    <MenuItem value="plum-v2">{modelLabel('plum-v2')}</MenuItem>
                    <MenuItem value="plumv2-fast">{modelLabel('plumv2-fast')}</MenuItem>
                    <MenuItem value="sunburst">{modelLabel('sunburst')}</MenuItem>
                    <MenuItem value="flare">{modelLabel('flare')}</MenuItem>
                  </Select>
                </FormControl>
              )}
              {!isBlueberryModel ? (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Proporcje (AR)</InputLabel>
                  <Select value={aspectRatio} label="Proporcje (AR)" onChange={(e) => setAspectRatio(e.target.value as any)}>
                    <MenuItem value="none">Nie zmieniaj (oryginalny)</MenuItem>
                    <MenuItem value="2:3">Portret (2:3)</MenuItem>
                    <MenuItem value="1:1">Kwadrat (1:1)</MenuItem>
                    <MenuItem value="4:3">Standard (4:3)</MenuItem>
                    <MenuItem value="9:16">Pion (9:16)</MenuItem>
                    <MenuItem value="16:9">Poziom (16:9)</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <div className="min-w-[220px] p-3 border border-dashed border-blue-200 rounded-lg">
                  <p className="text-xs text-gray-400">Proporcje wynikowe</p>
                  <p className="text-sm font-semibold text-gray-700">{derivedBlueberryAR(resolution)}</p>
                </div>
              )}
              {showGeminiResolution && (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>Rozdzielczość</InputLabel>
                  <Select
                    value={imageSize}
                    label="Rozdzielczość"
                    onChange={(e) => setImageSize(e.target.value as any)}
                    data-testid="stylista-select-image-size"
                  >
                    {currentGeminiResOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {/* OpenAI image models use pixel-discrete sizes + a quality knob — no 1K/2K/4K bucketing. */}
              {isOpenAIImageModel && (
                <>
                  <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel>Rozmiar ({isFlareModel ? 'Flare' : isSunburstModel ? 'Sunburst' : 'Plum v2'})</InputLabel>
                    <Select
                      value={plumSize}
                      label={`Rozmiar (${isFlareModel ? 'Flare' : isSunburstModel ? 'Sunburst' : 'Plum v2'})`}
                      onChange={(e) => setPlumSize(e.target.value as string)}
                      data-testid="stylista-select-plum-size"
                    >
                      {plumV2SizeOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Jakość</InputLabel>
                    <Select
                      value={plumQuality}
                      label="Jakość"
                      onChange={(e) => setPlumQuality(e.target.value as string)}
                      data-testid="stylista-select-plum-quality"
                    >
                      {(isSunburstModel || isFlareModel ? sunburstQualityOptions : plumV2QualityOptions).map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {isAdmin && supportsTransparentBackground && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={transparentBackground}
                          onChange={(e) => setTransparentBackground(e.target.checked)}
                        />
                      }
                      label="Przezroczyste tło (PNG)"
                    />
                  )}
                </>
              )}
              {isBlueberryModel && (
                <>
                  <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel>Rozdzielczość (Blueberry)</InputLabel>
                    <Select
                      value={resolution}
                      label="Rozdzielczość (Blueberry)"
                      onChange={(e) => setResolution(e.target.value as any)}
                      data-testid="stylista-select-blueberry-resolution"
                    >
                      {currentBlueberryResOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt === '1k' && '1K (1024x1024)'}
                          {opt === '2k' && '2K (2048x2048)'}
                          {opt === '2k_portrait' && '2K Pion (1:2) - 1024x2048'}
                          {opt === '4k' && '4K (4096x4096)'}
                          {opt === '4k_portrait' && '4K Pion (1:2) - 2048x4096'}
                          {opt === 'fashion_4k' && 'Fashion 4K Portret (2:3) - 2048x3072'}
                          {opt !== '1k' && opt !== '2k' && opt !== '2k_portrait' && opt !== '4k' && opt !== '4k_portrait' && opt !== 'fashion_4k' && opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {isAdmin && (
                    <FormControl sx={{ minWidth: 220 }}>
                      <InputLabel>Format wyjściowy</InputLabel>
                      <Select value={outputFormat} label="Format wyjściowy" onChange={(e) => setOutputFormat(e.target.value as any)}>
                        {currentBlueberryFormats.map(f => (
                          <MenuItem key={f} value={f}>{f}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </div>
          </div>
        </details>

        {/* Prompt section */}
        <details open className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
          <summary className="px-4 py-3 bg-white font-semibold text-gray-700 cursor-pointer flex items-center justify-between list-none select-none">
            Opis (prompt)
            <span className="text-xs text-gray-400">▼</span>
          </summary>
          <div className="p-4 border-t border-gray-100 bg-white flex flex-col gap-4">
            <FormControl sx={{ minWidth: 260 }}>
              <InputLabel>Szablon promptu</InputLabel>
              <Select
                value={promptPresetId}
                label="Szablon promptu"
                onChange={(e) => setPromptPresetId(e.target.value)}
              >
                {promptPresets.map(preset => (
                  <MenuItem key={preset.id} value={preset.id}>{preset.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {selectedPromptPreset?.description || (selectedPromptPreset?.prompt ? selectedPromptPreset.prompt : 'Brak dodatkowego tekstu z szablonu.')}
              </FormHelperText>
            </FormControl>
            {selectedPromptPreset?.prompt && (
              <div className="border border-dashed border-blue-200 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Tekst z wybranego szablonu:</p>
                <p className="text-sm text-gray-700">{selectedPromptPreset.prompt}</p>
              </div>
            )}
            {photoStyles.length > 0 && (
              <FormControl sx={{ minWidth: 260 }}>
                <InputLabel>Styl fotograficzny</InputLabel>
                <Select
                  value={photoStyleId}
                  label="Styl fotograficzny"
                  onChange={(e) => setPhotoStyleId(e.target.value)}
                >
                  <MenuItem value=""><em>Brak</em></MenuItem>
                  {photoStyles.map(s => (
                    <MenuItem key={s.style_id} value={s.style_id}>{s.name}</MenuItem>
                  ))}
                </Select>
                {photoStyleInjection && (
                  <FormHelperText sx={{ fontStyle: 'italic' }}>+ {photoStyleInjection}</FormHelperText>
                )}
              </FormControl>
            )}
            <TextField
              fullWidth
              multiline
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Dodaj własny opis (dokładny prompt, kolory, scenografia)…"
            />
          </div>
        </details>

        {/* Advanced Options */}
        <details open className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
          <summary className="px-4 py-3 bg-white font-semibold text-gray-700 cursor-pointer flex items-center justify-between list-none select-none">
            Advanced options
            <span className="text-xs text-gray-400">▼</span>
          </summary>
          <div className="p-4 border-t border-gray-100 bg-blue-50/20">
            <p className="text-sm text-gray-500 mb-2">
              Podgląd pełnego promptu i kolejności zdjęć, które zostaną wysłane do modelu (np. Blueberry / Strawberry Pro).
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Szablon promptu: <strong>{selectedPromptPreset?.label || 'Pusty'}</strong>
            </p>

            <div className="flex flex-col gap-2 mb-4">
              <p className="text-sm font-semibold text-gray-700">Kolejność zdjęć (FormData → backend):</p>
              {advancedSlotData.map(slot => {
                const baseLabel = slot.assignedIndex
                  ? `Image ${slot.assignedIndex}: ${slot.promptLine}`
                  : `Image slot (${slot.promptLine})`
                let label = baseLabel
                if (slot.key === 'poseImage' && poseMode === 'batch' && slot.present) {
                  label = `${baseLabel} — kolejka ${slot.files.length} póz`
                } else if (slot.key === 'faceImage' && faceMode === 'batch' && slot.present) {
                  label = `${baseLabel} — kolejka ${slot.files.length} twarzy`
                } else if (slot.key === 'garmentTopImage' && garmentTopMode === 'batch' && slot.present) {
                  label = `${baseLabel} — kolejka ${slot.files.length} ubrań (góra)`
                } else if (slot.key === 'garmentBottomImage' && garmentBottomMode === 'batch' && slot.present) {
                  label = `${baseLabel} — kolejka ${slot.files.length} ubrań (dół)`
                }
                const previews = previewUrls[slot.key] || []
                const maxThumbs = 4
                return (
                  <div
                    key={slot.key}
                    className={`border rounded-lg p-3 flex gap-3 flex-wrap items-center ${
                      slot.present ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-transparent'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${slot.present ? 'text-gray-800' : 'text-gray-300'}`}>
                        {label}
                      </p>
                      {slot.present ? (
                        slot.files.length <= 1 ? (
                          <p className="text-xs text-gray-400">Plik: {slot.files[0]?.name || '—'}</p>
                        ) : (
                          <>
                            <p className="text-xs text-gray-400">Lista plików (kolejność wysyłania):</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {slot.files.map((file, idx) => {
                                const chipColor =
                                  slot.key === 'poseImage' ? 'bg-blue-100 text-blue-700'
                                  : slot.key === 'faceImage' ? 'bg-purple-100 text-purple-700'
                                  : slot.key === 'garmentTopImage' ? 'bg-green-100 text-green-700'
                                  : slot.key === 'garmentBottomImage' ? 'bg-cyan-100 text-cyan-700'
                                  : 'bg-gray-100 text-gray-600'
                                return (
                                  <span
                                    key={`${slot.key}-${file.name}-${idx}`}
                                    className={`text-xs px-2 py-0.5 rounded-full ${chipColor}`}
                                  >
                                    #{idx + 1} {file.name}
                                  </span>
                                )
                              })}
                            </div>
                          </>
                        )
                      ) : (
                        <p className="text-xs text-gray-300">Brak zdjęcia dla tego slotu</p>
                      )}
                    </div>
                    {slot.present && previews.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {previews.slice(0, maxThumbs).map((url, idx) => (
                          <img
                            key={`${slot.key}-preview-${idx}`}
                            src={url}
                            alt={`${slot.title}-${idx}`}
                            className="w-14 h-14 object-cover rounded border border-gray-200"
                          />
                        ))}
                        {previews.length > maxThumbs && (
                          <span className="text-xs text-gray-400 self-center">+{previews.length - maxThumbs} więcej</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {isAdmin && (
              <div className="mt-2">
                <p className="text-sm font-semibold text-gray-700 mb-2">Pełny prompt wysyłany do modelu:</p>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  value={(fullPromptOverride !== '' ? fullPromptOverride : advancedPrompt)}
                  onChange={(e) => setFullPromptOverride(e.target.value)}
                  placeholder="Prompt będzie widoczny po uzupełnieniu pól i dodaniu zdjęć."
                  helperText="Tylko admin: możesz nadpisać prompt wysyłany do modelu. Zostaw puste, aby użyć promptu generowanego automatycznie."
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      borderRadius: 1,
                    }
                  }}
                />
              </div>
            )}
          </div>
        </details>

        {/* Generate button */}
        {noModelsAvailable && (
          <div className="mb-2 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
            Brak dostępnych modeli AI. Skontaktuj się z administratorem.
          </div>
        )}
        <div className="sticky bottom-0 z-20 -mx-4 px-4 py-3 bg-white/95 backdrop-blur-sm border-t border-gray-100 flex gap-3 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:border-0 sm:mb-2">
          <button
            onClick={handleGenerate}
            disabled={!canGenerate}
            data-testid="stylista-generate-button"
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors min-h-[52px] text-lg shadow-md"
          >
            <SparklesIcon />
            {isGenerating ? `Generowanie... ${elapsedSeconds}s` : 'Generuj'}
          </button>
          <button
            onClick={resetState}
            disabled={isGenerating}
            className="px-6 py-3.5 border border-gray-300 rounded-xl text-gray-600 hover:border-gray-400 disabled:opacity-40 transition-colors min-h-[52px]"
          >
            Reset
          </button>
        </div>
        {isGenerating && (
          <LinearProgress sx={{ borderRadius: 1, mb: 4 }} />
        )}
        {!isGenerating && <div className="mb-4" />}

        {/* Status alerts */}
        {generationResult.status === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6 text-sm text-red-700">
            {generationResult.message || 'Błąd generacji'}
          </div>
        )}
        {generationResult.status === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-6 text-sm text-green-700">
            Wygenerowano {generationResult.imagesGenerated || 0} obraz(ów)
          </div>
        )}

        {/* Model response (non-batch) */}
        {!batchInfo?.enabled && generationResult.modelResponse && (
          <div className="border border-gray-200 rounded-xl p-4 mb-6 bg-white">
            <p className="text-sm font-semibold text-gray-700 mb-2">Pełna odpowiedź modelu:</p>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={generationResult.modelResponse}
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  borderRadius: 1,
                }
              }}
            />
          </div>
        )}

        {/* Batch queue */}
        {batchInfo?.enabled && (
          <div className="border border-gray-200 rounded-xl p-4 mb-6 bg-white">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">
              Kolejka zadań (Face/Pose/Ubrania{multiModelEnabled ? '/Modele' : ''})
            </h2>
            <p className="text-sm text-gray-500 mb-2">
              Postęp: {batchInfo.completed}/{batchInfo.total}
            </p>
            <LinearProgress
              variant="determinate"
              value={batchInfo.total ? Math.min(100, (batchInfo.completed / batchInfo.total) * 100) : 0}
              sx={{ mb: 2 }}
            />
            <div className="flex flex-col gap-3">
              {batchInfo.results.length ? batchInfo.results.map((job, idx) => (
                <div key={`${job.poseName || 'no-pose'}-${idx}`} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex justify-between flex-wrap gap-2 mb-1">
                    <div className="flex flex-col">
                      <p className="text-sm font-semibold text-gray-800">
                        #{idx + 1} {job.poseName ? `Poza: ${job.poseName}` : 'Poza: brak pliku'}
                      </p>
                      <p className="text-xs text-gray-400">{job.faceName ? `Twarz: ${job.faceName}` : 'Twarz: brak pliku'}</p>
                      {job.modelName && (
                        <p className="text-xs text-gray-400">Model: {modelLabel(job.modelName)}</p>
                      )}
                      <p className="text-xs text-gray-400">{job.garmentTopName ? `Góra: ${job.garmentTopName}` : 'Góra: brak pliku'}</p>
                      <p className="text-xs text-gray-400">{job.garmentBottomName ? `Dół: ${job.garmentBottomName}` : 'Dół: brak pliku'}</p>
                    </div>
                    <span className={`self-start text-xs font-semibold px-2 py-1 rounded-full ${job.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {job.success ? 'SUKCES' : 'BŁĄD'}
                    </span>
                  </div>
                  {!job.success && job.message && (
                    <p className="text-xs text-red-500">{job.message}</p>
                  )}
                  {job.modelResponse && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-400 mb-1">Pełna odpowiedź modelu:</p>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={job.modelResponse}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            borderRadius: 1,
                          }
                        }}
                      />
                    </div>
                  )}
                  {job.previewUrls?.length ? (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {job.previewUrls.slice(0, 3).map((url, i) => (
                        <img
                          key={`${job.poseName || 'no-pose'}-preview-${i}`}
                          src={url}
                          alt={`preview-${i}`}
                          className="w-[72px] h-[72px] object-cover rounded border border-gray-200"
                        />
                      ))}
                      {job.previewUrls.length > 3 && (
                        <span className="text-xs text-gray-400 self-center">+{job.previewUrls.length - 3} więcej</span>
                      )}
                    </div>
                  ) : null}
                </div>
              )) : (
                <p className="text-xs text-gray-400">Przetwarzanie w toku…</p>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {generationResult.status === 'success' && resultPreviews.length > 0 && (
          <div className="border border-gray-200 rounded-xl p-4 mb-6 bg-white">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Wyniki</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {resultPreviews.map((item, idx) => {
                const meta = item.metadata
                const showMeta = meta && (meta.size || typeof meta.generation_time === 'number')
                return (
                  <div key={idx}>
                    <img src={item.url} alt={`gen-${idx}`} className="w-full rounded-lg shadow-sm" />
                    {/* Meta UNDER the image: dimensions + generation time only. */}
                    {showMeta && (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-1">
                        {meta.size && <span>Wymiary: {meta.size}</span>}
                        {typeof meta.generation_time === 'number' && <span>Czas: {meta.generation_time}s</span>}
                      </div>
                    )}
                    {item.modelName && !showMeta && (
                      <p className="text-xs text-gray-400 mt-1">Model: {modelLabel(item.modelName)}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <GalleryModal
          open={galleryModalType !== null}
          onClose={() => setGalleryModalType(null)}
          onSelect={handleGallerySelect}
          items={getGalleryItems()}
          loading={!!galleryLoading[galleryModalType || '']}
          title={getGalleryTitle()}
          defaultGroup={galleryModalType === 'faces' ? 'european' : null}
          defaultGender={
            galleryModalType === 'faces' ? 'female'
            : galleryModalType === 'poses' ? 'woman'
            : galleryModalType === 'figures' ? 'woman'
            : galleryModalType === 'garments' ? 'female'
            : null
          }
          defaultAge={galleryModalType === 'faces' ? '30plus' : null}
          defaultBodyPart={galleryModalType === 'poses' ? 'full-body' : null}
          imageObjectFit={galleryModalType === 'figures' || galleryModalType === 'poses' ? 'contain' : 'cover'}
          imageAspectRatio={galleryModalType === 'figures' || galleryModalType === 'poses' ? '2/3' : undefined}
          groupLabel={
            galleryModalType === 'poses' ? 'Widok:'
            : galleryModalType === 'faces' ? 'Pochodzenie:'
            : galleryModalType === 'garments' ? 'Typ:'
            : 'Kategoria:'
          }
        />
      </div>
    </>
  )
}

const StylistaPage: React.FC = () => (
  <StylistaProvider>
    <StylistaContent />
  </StylistaProvider>
)

export default StylistaPage
